import { PLAYER_COLORS } from "@/lib/api";
import { nameOf, pieceLabel } from "@/components/game/SeaSquare";

const colOf = (key) => Number(key.split(",")[1]);
const title = (k) => (k || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// TERRAIN 2 of 3: one line per flooded square (rows from the coast, as everywhere), plus how many
// squares Sea Walls hold back. The rise summary is {sea_level, flooded: [{cell, lost}], held_back}.
const rowCol = (key, rows) => {
  const [r, c] = key.split(",").map(Number);
  return rows ? `row ${rows - 1 - r}, col ${c + 1}` : key;
};

function columnLines(summary, who, rows) {
  const lines = [];
  for (const t of summary.flooded || []) {
    const text = t.lost ? `${who(t.lost.owner)}'s ${pieceLabel(t.lost)} lost` : "flooded";
    lines.push({ col: colOf(t.cell), key: t.cell, text: `${rowCol(t.cell, rows)}: ${text}` });
  }
  const held = (summary.held_back || []).length;
  if (held) lines.push({ col: 99, key: "held", text: `Sea Walls hold back ${held} square${held === 1 ? "" : "s"}` });
  return lines.sort((x, y) => x.col - y.col);
}

function lossesByOwner(summary) {
  const all = summary.flooded || (summary.affected || []).flatMap((a) => a.taken || []);
  const groups = {};
  for (const t of all) {
    if (!t.lost?.owner) continue;
    (groups[t.lost.owner] ||= []).push({ key: t.cell, label: pieceLabel(t.lost), points: t.lost.points ?? 0 });
  }
  return Object.entries(groups);
}

// Shown in the weather panel after a Sea Level Rise animation; Continue closes it.
export default function SeaRiseSummary({ summary, players, maxSlr, onContinue, gridRows = null }) {
  const who = (c) => nameOf(players, c);
  const lines = columnLines(summary, who, gridRows);
  const groups = lossesByOwner(summary);
  return (
    <div data-testid="sea-rise-summary-layer">
      <div
        role="region"
        aria-label="The sea rose"
        data-testid="sea-rise-summary"
        className="flex flex-col gap-3 animate-in fade-in duration-200"
      >
        <h2 data-testid="sea-rise-title" style={{ fontSize: 16, lineHeight: "20px", fontWeight: 700, color: "var(--ink)" }}>
          The sea rose ({summary.events} of {maxSlr}) · sea level {summary.sea_level ?? summary.events}
        </h2>
        <ul className="flex flex-col gap-1" style={{ fontSize: 12.5, lineHeight: "16px", color: "var(--ink-2)" }}>
          {lines.length === 0 && <li data-testid="sea-rise-line-none">Nothing changed on the coast.</li>}
          {lines.map((l, i) => (
            <li key={i} data-testid={`sea-rise-line-${i}`}>
              {l.text}
            </li>
          ))}
        </ul>
        {groups.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="section-label">Losses</div>
            {groups.map(([color, items]) => (
              <div key={color} data-testid={`sea-rise-losses-${color}`} className="flex items-start gap-2">
                <span className="shrink-0 mt-0.5" style={{ width: 12, height: 12, borderRadius: 3, background: PLAYER_COLORS[color] }} />
                <div className="flex-1 min-w-0" style={{ fontSize: 12.5 }}>
                  <div style={{ fontWeight: 600, color: "var(--ink)" }}>{who(color)}</div>
                  {items.map((it) => (
                    <div key={it.key} className="flex justify-between gap-3" style={{ color: "var(--ink-3)" }}>
                      <span className="truncate">{it.label}</span>
                      <span data-testid={`sea-rise-points-${it.key}`} className="shrink-0" style={{ fontFamily: "var(--font-mono)" }}>{it.points} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button type="button" data-testid="sea-rise-continue" className="btn-primary btn-compact w-full" style={{ height: 38 }} onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}
